import { useEffect, useRef, useState } from "react";
import { listGrantViewers } from "../../lib/accessClient.js";
import { ACTIVITY_CAPABILITIES, RAW_PROMPT_CAPABILITY, listViewerActivityGrants } from "../../lib/activityConfiguration.js";
import { hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { roleLabel } from "../../lib/userRoles.js";
import { recipientName, scopeName } from "./ActivityConfigurationFields.jsx";
import OverlayDialog from "../OverlayDialog.jsx";
import Button from "../ui/Button.jsx";
import Notice from "../ui/Notice.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const NOTE_CLASS = "text-[12px] leading-[1.45] text-[color:var(--muted)]";
const LABEL_CLASS = "text-[12px] font-medium text-[color:var(--muted)]";
const PAGE_SIZE = 25;
const EMPTY_PAGE = { grants: [], nextPageToken: "", inheritanceStatus: "", enforcementEnabled: null, checkedAt: 0 };

function UserScopeDetails({ viewer, membershipRole, onOpenGrant }) {
    const request = useRef(0);
    const [page, setPage] = useState({ ...EMPTY_PAGE, loading: true, error: "" });
    const load = async (token = "") => {
        const current = ++request.current;
        setPage(previous => ({ ...(token ? previous : EMPTY_PAGE), loading: true, error: "" }));
        try {
            const result = await listViewerActivityGrants({ viewerUserId: viewer.viewerUserId, pageSize: PAGE_SIZE, pageToken: token });
            if (current !== request.current) return;
            setPage(previous => ({ ...result, loading: false, error: "", checkedAt: Date.now(),
                grants: token ? [...previous.grants, ...result.grants] : result.grants }));
        } catch (error) {
            if (current !== request.current) return;
            if (token && ["INVALID_ARGUMENT", "ABORTED"].includes(error?.grpcCode)) {
                setPage({ ...EMPTY_PAGE, loading: false, error: "Grant details changed while loading. Retry to load the current grants." });
            } else {
                setPage(previous => ({ ...previous, loading: false, error: "Grant details are unavailable right now. Try again later." }));
            }
        }
    };
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        loadRef.current();
        return () => { request.current += 1; };
    }, [viewer.viewerUserId]);
    useEffect(() => {
        const expiries = page.grants.map(row => Number(row.grant?.expiresAtUnixMs)).filter(value => value > 0);
        if (!expiries.length || page.loading || page.error) return undefined;
        const timer = setTimeout(() => loadRef.current(), Math.min(2147483647, Math.max(1000, Math.min(...expiries) - Date.now())));
        return () => clearTimeout(timer);
    }, [page.grants, page.loading, page.error]);
    const enforcementKnown = typeof page.enforcementEnabled === "boolean";
    const inheritedKnown = page.inheritanceStatus === "resolved";
    // Said once, over the grants, rather than under each kind of grant.
    const inheritanceNote = inheritedKnown || page.loading || page.error ? null
        : page.inheritanceStatus === "viewer_unmapped" ? "Group access has not been confirmed: this person isn't matched to a directory person. Any direct grants are shown below."
            : page.inheritanceStatus === "no_active_generation" ? "Group access has not been confirmed: group details are unavailable right now. Any direct grants are shown below."
                : "Group access has not been confirmed. Any direct grants are shown below.";
    return <section aria-label="Selected user access" className="grid min-w-0 content-start gap-[18px]">
        {enforcementKnown ? <Notice title={page.enforcementEnabled ? "Activity grants enforced" : "ABAC is off"}>
            {page.enforcementEnabled
                ? "Only capability grants provide activity access. Tenant Admin, SecOps, and Detection Engineer roles do not bypass activity scopes."
                : "Existing role-based activity access remains unchanged. The grants below apply once ABAC is on."}
        </Notice> : !page.loading && !page.error ? <Notice tone="warning">The tenant's enforcement setting could not be confirmed. These grants do not establish current activity access.</Notice> : null}
        <div className="flex flex-wrap items-baseline justify-between gap-x-[16px] gap-y-[2px] border-b border-[color:var(--line-soft)] pb-[14px]">
            <span className={LABEL_CLASS}>Operational role</span>
            <span className="text-[13px] font-medium text-[color:var(--ink)]">{membershipRole ? roleLabel(membershipRole) : "Role details unavailable"}</span>
            <p className={`m-0 basis-full ${NOTE_CLASS}`}>Set in Users, separately from these grants.</p>
        </div>
        <section aria-label="Configured grants" aria-busy={page.loading} className="grid gap-[12px]">
            <h3 className="m-0 text-[14px] font-medium text-[color:var(--ink)]">{page.enforcementEnabled === false ? "Prepared capability grants" : "Capability grants"}</h3>
            {inheritanceNote ? <p className={`m-0 ${NOTE_CLASS}`}>{inheritanceNote}</p> : null}
            <div className="grid">
                {ACTIVITY_CAPABILITIES.map(capability => {
                    const rows = page.grants.filter(row => row.grant?.capability === capability.value);
                    return <div key={capability.value} aria-label={capability.label} className="grid gap-[8px] border-b border-[color:var(--line-soft)] py-[12px] last:border-b-0">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-[16px] gap-y-[2px]">
                            <h4 className="m-0 text-[13px] font-medium text-[color:var(--ink)]">{capability.label}</h4>
                            {!rows.length && !page.loading && !page.error ? <span className={NOTE_CLASS}>{page.nextPageToken ? "No grants of this type on the loaded pages." : inheritedKnown ? "No grants of this type." : "No direct grants of this type."}</span> : null}
                            <p className={`m-0 basis-full ${NOTE_CLASS}`}>
                                {capability.description}
                                {capability.value !== RAW_PROMPT_CAPABILITY ? " Prompt and response grants also include metadata within their own scopes." : null}
                            </p>
                        </div>
                        {rows.map(row => <div key={row.id} className="flex flex-wrap items-start justify-between gap-x-[16px] gap-y-[4px] rounded-[8px] border border-[color:var(--line-soft)] px-[12px] py-[9px] text-[12.5px] text-[color:var(--ink)]">
                            <div className="grid min-w-0 gap-[2px]">
                                <strong className="font-medium">{scopeName(row.grant.scope)}</strong>
                                <span className={NOTE_CLASS}>
                                    {row.recipient?.directoryGroup ? <>Through group: {recipientName(row.recipient)}</> : "Assigned directly"}
                                    {Number(row.grant.expiresAtUnixMs) > 0 ? <>. Expires {new Date(Number(row.grant.expiresAtUnixMs)).toLocaleString()}.</> : null}
                                </span>
                            </div>
                            {onOpenGrant ? <Button variant="ghost" size="sm" className="-mr-[8px]" onClick={() => onOpenGrant(row)}>View grant</Button> : null}
                        </div>)}
                    </div>;
                })}
            </div>
            {page.loading ? <span role="status" className={NOTE_CLASS}>Loading grants...</span> : null}
            {page.error ? <Notice tone="error" action={<Button size="sm" onClick={() => load(page.nextPageToken)}>Retry grants</Button>}>{page.error}</Notice> : null}
            {!page.loading && !page.error && !page.grants.length && inheritedKnown && page.enforcementEnabled === true ? <p className={`m-0 ${NOTE_CLASS}`}>No activity access. This person has no metadata or prompt grants.</p> : null}
            {page.nextPageToken && !page.error ? <Button size="sm" className="justify-self-start" disabled={page.loading} onClick={() => load(page.nextPageToken)}>Load more grants</Button> : null}
        </section>
        <footer className="flex flex-wrap items-center justify-between gap-[10px] border-t border-[color:var(--line-soft)] pt-[14px]">
            <span className={NOTE_CLASS}>
                {page.grants.length ? `Showing ${page.grants.length} ${page.grants.length === 1 ? "grant" : "grants"}${page.nextPageToken ? " so far" : ""}. ` : ""}
                {page.checkedAt ? `Checked ${new Date(page.checkedAt).toLocaleString()}.` : null}
            </span>
            <Button size="sm" disabled={page.loading} onClick={() => load()}>Refresh access</Button>
        </footer>
    </section>;
}

export default function UserEffectiveAccessDialog({ user, onClose, onOpenGrant }) {
    const caps = useMyPermissions();
    const allowed = hasSensitivePermission(caps, PERMISSIONS.GRANTS_READ)
        && hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const [retry, setRetry] = useState(0);
    const [lookup, setLookup] = useState({ loading: true, viewer: null, error: "" });
    useEffect(() => {
        if (!allowed) return undefined;
        let cancelled = false;
        setLookup({ loading: true, viewer: null, error: "" });
        const email = user.email?.trim();
        if (!email) {
            setLookup({ loading: false, viewer: null, error: "Add an email address to this user to check their activity access." });
            return undefined;
        }
        listGrantViewers({ pageSize: PAGE_SIZE, search: email }).then((page) => {
            if (cancelled) return;
            const matches = page.viewers.filter((viewer) => viewer.displayName.trim().toLowerCase() === email.toLowerCase());
            if (matches.length !== 1 || !matches[0]?.viewerUserId || page.nextPageToken || page.totalCount > page.viewers.length) {
                setLookup({ loading: false, viewer: null, error: matches.length > 1 || page.nextPageToken || page.totalCount > page.viewers.length
                    ? "We could not confirm this person's account. Check their email address and try again."
                    : "Activity access is not available for this person yet." });
                return;
            }
            setLookup({ loading: false, viewer: matches[0], error: "" });
        }, () => {
            if (!cancelled) setLookup({ loading: false, viewer: null, error: "We couldn't check this user's activity access. Try again in a moment." });
        });
        return () => { cancelled = true; };
    }, [allowed, retry, user.userId, user.email]);
    return (
        <OverlayDialog open onClose={onClose} eyebrow={user.email || null} title="Activity access" width="640px" analyticsId={DIALOG.ACTIVITY_ACCESS}>
            <div>
            {!allowed ? <p className={NOTE_CLASS}>You do not have permission to view this person's activity access.</p> : (
                <div aria-busy={lookup.loading}>
                    {lookup.loading ? <p role="status" className={NOTE_CLASS}>Checking activity access...</p> : lookup.error ? (
                        <Notice tone="error" action={<Button size="sm" onClick={() => setRetry((current) => current + 1)}>Try again</Button>}>{lookup.error}</Notice>
                    ) : <UserScopeDetails key={lookup.viewer.viewerUserId} viewer={lookup.viewer} membershipRole={user.roleSlug} onOpenGrant={onOpenGrant} />}
                </div>
            )}
            </div>
        </OverlayDialog>
    );
}
